import {
  Guest,
  Reservation,
  Seat,
  SeatType,
  Staff,
} from "@/graphql/types";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import Button from "../common/button/button";
import Modal from "../common/modal/modal";
import SeatsDisplay from "../seat/seatsDisplay";
import * as GraphQLClient from "@apollo/client"
import { AssignSeats as AssignSeatsMutation } from "@/graphql/documentNode";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";

type Props = {
  reservations: Reservation[];
  guests: Guest[];
  seats: Seat[];
  staffList: Staff[];
  seatTypes: SeatType[];
  onSubmit: (guests: Guest[]) => Promise<void>
  onClose: () => void;
};

const AssignSeats = (props: Props) => {
  const snackBar = useSnackBar({})

  const [seats, setSeats] = useState(props.seats)
  const [guests, setGuests] = useState(props.guests)
  const [reservations, setReservations] = useState(props.reservations)

  const [showAlert, setShowAlert] = useState(false)


  const [selectedSeatIndex, setSelectedSeatIndex] = useState(-1)
  const [selectedGuestIndex, setSelectedGuestIndex] = useState(-1)


  const onGuestClicked = useCallback(
    (guest: Guest, confirmed?: boolean) => {
      if (guest.selfReserved && !confirmed) {
        setShowAlert(true)
      }
      const guestIndex = guests.findIndex(g => g.id === guest.id)
      
      // 座席選択済の場合
      if (selectedSeatIndex > -1) {
        const seat = seats[selectedSeatIndex]
        const seatsCopy = [...seats]
        const guestsCopy = [...guests]
        if (seat.reservationId === guest.reservationId) {
          seatsCopy[selectedSeatIndex] = {
            ...seatsCopy[selectedSeatIndex],
            reservationId: 0,
          }
          guestsCopy[guestIndex] = {
            ...guestsCopy[guestIndex],
            seatId: 0,
          }
        } else {
          // 座席・観客のペアリング関係を解除する
          const oldGuestIndex = guestsCopy.findIndex(g => g.seatId === seat.id)
          guestsCopy[oldGuestIndex] = {
            ...guestsCopy[oldGuestIndex],
            seatId: 0,
          }
          const oldSeatIndex = seatsCopy.findIndex(s => s.id === guest.seatId)
          seatsCopy[oldSeatIndex] = {
            ...seatsCopy[oldSeatIndex],
            reservationId: 0,
          }

          seatsCopy[selectedSeatIndex] = {
            ...seatsCopy[selectedSeatIndex],
            reservationId: guest.reservationId,
          }
          guestsCopy[guestIndex] = {
            ...guestsCopy[guestIndex],
            seatId: seat.id ?? 0,
          }
        }
        setSeats(seatsCopy)
        setGuests(guestsCopy)
        setSelectedGuestIndex(-1);
      } else {
        // 座席未選択の場合
        if (selectedGuestIndex === guestIndex) {
          setSelectedGuestIndex(-1)
        } else {
          setSelectedGuestIndex(guestIndex)
        }
      }
    },
    [guests, seats, selectedGuestIndex, selectedSeatIndex],
  );
  console.log(seats.map(s => s.id))
  const reservationComponents = useMemo(() => {
    const guestComponent = (guest: Guest) => {
      console.log(guest.seatId)
      const pairSeat = seats.find((seat) => seat.id === guest.seatId);
      return pairSeat ? (
        <div className="guest-button">
          <Button
            buttonType={
              selectedGuestIndex > -1 && guests[selectedGuestIndex].id === guest.id
                ? "checked"
                : "powerful-red-reverse"
            }
            label={pairSeat.seatName + (guest.selfReserved ? "(変更不可)" : "")}
            disabled={!!guest.selfReserved}
            onClick={() => {
              onGuestClicked(guest);
            }}
          />
        </div>
      ) : (
        <div className="guest-button">
          <Button
            buttonType={
              selectedGuestIndex > -1 && guests[selectedGuestIndex].id === guest.id
                ? "checked"
                : "default-red-red-shadow"
            }
            label="未着席"
            disabled={false}
            onClick={() => {
              onGuestClicked(guest);
            }}
          />
        </div>
      );
    };

    const reservationComponent = (reservation: Reservation) => {
      const reservationGuests = guests.filter(g => g.reservationId === reservation.id)
      const guestComponents = reservationGuests.map((guest) => guestComponent(guest));
      const pairStaff = props.staffList?.find(
        (staff) => staff.id === reservation.invitationId,
      );

      return (
        <ReservationComponent className={`${reservationGuests.some(guest => !seats.find((seat) => seat.id === guest.seatId)) ? 'active' : 'inactive'}`}>
          <div className="reservation-name">{reservation.name}</div>
          {pairStaff ? (
            <div className="staff-name">{pairStaff.name}</div>
          ) : (
            <></>
          )}
          {reservation.note ? (
            <div className="note">{reservation.note}</div>
          ) : (
            <></>
          )}
          <div className="guests">{guestComponents}</div>
        </ReservationComponent>
      );
    };

    return [...reservations].sort((a, b) => a.phonetic.localeCompare(b.phonetic, 'ja')).map((reservation) =>
      reservationComponent(reservation),
    );
  }, [guests, onGuestClicked, props.staffList, reservations, seats, selectedGuestIndex]);

  const onSeatClicked = useCallback(
    (seat: Seat | undefined) => {
      if (!seat) {
        setSelectedSeatIndex(-1)
        return false
      }
      if(seat.locked || seat.type === -1) {
        return false
      }
      const seatIndex = seats.findIndex(s => s.row === seat.row && s.column === seat.column)

      // 観客選択済の場合
      if (selectedGuestIndex > -1) {
        const selectedGuest = guests[selectedGuestIndex]
        const seatsCopy = [...seats]
        const guestsCopy = [...guests]
        if (seatsCopy[seatIndex].reservationId === selectedGuest.reservationId) {
          seatsCopy[seatIndex] = {
            ...seatsCopy[seatIndex],
            reservationId: 0,
          }
          guestsCopy[selectedGuestIndex] = {
            ...guestsCopy[selectedGuestIndex],
            seatId: 0,
          }
        } else {
          // 座席・観客のペアリング関係を解除する
          const seat = seatsCopy[seatIndex]
          const oldGuestIndex = guestsCopy.findIndex(g => g.seatId === seat.id)
          guestsCopy[oldGuestIndex] = {
            ...guestsCopy[oldGuestIndex],
            seatId: 0,
          }
          const oldSeatIndex = seatsCopy.findIndex(s => s.id === selectedGuest.seatId)
          seatsCopy[oldSeatIndex] = {
            ...seatsCopy[oldSeatIndex],
            reservationId: 0,
          }

          seatsCopy[seatIndex] = {
            ...seatsCopy[seatIndex],
            reservationId: selectedGuest.reservationId,
          }
          guestsCopy[selectedGuestIndex] = {
            ...guestsCopy[selectedGuestIndex],
            seatId: seat.id ?? 0,
          }
        }
        setSeats(seatsCopy)
        setGuests(guestsCopy)
        return false
      } else {
        // 観客未選択の場合
        console.log(selectedSeatIndex, seatIndex)
        if (selectedSeatIndex === seatIndex) {
          setSelectedSeatIndex(-1)
        } else {
          setSelectedSeatIndex(seatIndex)
        }
        return true
      }
    },
    [guests, seats, selectedGuestIndex, selectedSeatIndex],
  );
    
  const [assignSeats] = GraphQLClient.useMutation(AssignSeatsMutation, {
    onCompleted: (data) => {  
      snackBar.onMutationSuccess()
    },
    onError: (err) => {
      console.log(err)
      snackBar.onMutationError()
    }
  })

  const submit = useCallback(async () => {
    snackBar.loadStart()
    await assignSeats({
      variables: {
        input: {
          seats: seats.map(s => ({
            id: s.id,
            reservationId: s.reservationId,
          })),
          guests: guests.map(g => ({
            id: g.id,
            seatId: g.seatId,
          }))
        }
      }
    })
    props.onClose()
  }, [assignSeats, guests, props, seats, snackBar])


  const buttons = useMemo(() => {
    return (
      <div className="buttons">
        <Button
          label="キャンセル"
          buttonType="powerful-red"
          onClick={props.onClose}
        />
        <Button
          label="保存"
          buttonType="powerful-red"
          onClick={submit}
        />
      </div>
    )
  }, [props.onClose, submit])

  const lockRule = useCallback((seat: Seat) => {
    const reservation = props.reservations.find(r => r.id === seat.reservationId)
    if (!reservation) {
      return false
    }
    if(seat.type === -1 || seat.locked) {
      return false
    }
    const guest = guests.find(g => g.seatId === seat.id)
    if (reservation.confirmed || guest?.selfReserved) {
      return true
    }
    return false
  }, [guests, props.reservations])

  return (
    <Modal maxHeight={1300} width={1300} onClose={props.onClose}>
      <>
        <AssignSeatsStyle>
          <Container>
            <SeatsContainer>
              <SeatsDisplay
                seats={seats}
                size="large"
                seatTypes={props.seatTypes}
                reservations={props.reservations}
                onSeatClicked={onSeatClicked}
                lockRule={lockRule}
                colorRule={(seat) => undefined}
              />
            </SeatsContainer>
            <ReservationContainer>{reservationComponents}</ReservationContainer>
          </Container>
          {buttons}
        </AssignSeatsStyle>
        {
          showAlert ? (
            <Modal>
              <>
                <div>確認</div>
                <div>選択しようとしている座席・観客は、既に</div>
              </>
            </Modal>
          ) : <></>
        }
        {snackBar.component}
      </>
    </Modal>
  );
};

export default AssignSeats;

const ReservationComponent = styled.div`
  font-size: 16px;
  text-align: center;
  width: 200px;
  border-radius: 8px;
  margin: 10px auto 10px auto;
  padding: 5px 0 5px 0;
  &.active {
      background-color: black;
      color: white;
  }
  &.inactive {
      background-color: #b1aeae;
  }
  .reservation-name {
    font-weight: bold;
    margin: 5px auto 5px auto;
    overflow-x: hidden;
    text-overflow: ellipsis;
    width: 180px;
  }
  .staff-name {
    margin: 5px auto 5px auto;
    overflow-x: hidden;
    text-overflow: ellipsis;
    width: 180px;
  }
  .note {
    font-size: 12px;
    width: 180px;
    margin: 5px auto 5px auto;
  }
  .guests {
    .guest-button {
      margin: 10px auto 10px auto;
    }
  }
`;

const ReservationContainer = styled.div`
  height: 800px;
  overflow-y: scroll;
  width: 250px;
`;

const SeatsContainer = styled.div`
  height: 800px;
  width: 800px;
  overflow-x: scroll;
  overflow-y: scroll;
`;

const Container = styled.div`
  height: 800px;
  width: 1200px;
  display: flex;
  justify-content: center;
  gap: 100px;
`;

const AssignSeatsStyle = styled.div`
  .buttons {
    display: flex;
    width: 300px;
    align-items: center;
    justify-content: center;
    margin: auto;
    gap: 20px;
  }
`